import { baseURL } from 'app/client';
import { createAuthInterceptor } from 'services/swagger-api/authInterceptor';

/**
 * Paramètres de la commande courante de la liste, transmis au bouton « Exporter ».
 * `filters` est indexé par facet.key (comme le params.filters de fetchData) — la vue le
 * mappe vers le FiltersDto avant l'appel CSV, exactement comme pour la liste.
 */
export interface ListExportParams {
  searchString?: string;
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc';
  filters?: Record<string, any>;
}

/**
 * Construit la commande CSV à partir des paramètres courants + des filtres déjà mappés
 * vers le FiltersDto. pageNumber/pageSize sont ignorés côté back (export complet).
 */
export function buildExportCommand(
  params: ListExportParams,
  mappedFilters?: Record<string, any>
) {
  return {
    pageNumber: 1,
    pageSize: 1,
    searchString: params.searchString,
    sortBy: params.sortBy,
    sortDirection: params.sortDirection,
    filters: mappedFilters
  };
}

const authFetch = createAuthInterceptor();

/**
 * Rejoue la commande de liste sur la route .../csv et déclenche le téléchargement du
 * fichier. Le body est le même GetListeGeneriqueCommand que la liste (pageNumber/pageSize
 * ignorés côté back — export complet, plafond 50 000 lignes).
 *
 * @param path chemin complet de la route CSV, ex. /api/backoffice/contrats/admin/liste/csv
 * @param command commande de liste (searchString, filters, sortBy, sortDirection)
 */
export async function exportListCsv(path: string, command: any): Promise<void> {
  const res = await authFetch(`${baseURL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(command)
  });

  if (!res.ok) {
    // Erreur ABP éventuelle (403, volume dépassé…) : on remonte le message affichable
    let message: string | undefined;
    try {
      const body = await res.clone().json();
      message = body?.error?.message;
    } catch {
      /* réponse non-JSON */
    }
    throw new Error(message || `Export échoué (${res.status})`);
  }

  const blob = await res.blob();
  const filename =
    res.headers.get('Content-Disposition')?.match(/filename\*?="?([^";]+)/i)?.[1] ?? 'export.csv';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = decodeURIComponent(filename);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
